/**
 * On-session payment helpers (Option A): redirect URL, return URL encoding, outcome resolution.
 * @see docs/room booking page/STEP_4_4_ON_SESSION_PAYMENTS_TASKMASTER.md — Task 1
 */

import { getMewsApplicationsBaseUrl } from './client';
import type {
  GetReservationGroupResponse,
  GetReservationPriceResponse,
} from './types';

export type MewsPaymentMode = 'on-session' | 'authorize';

/** Sum of AmountToChargeOnConfirmation across aligned price rows (settlement currency). */
export interface AmountToCharge {
  grossValue: number;
  currency: string;
}

export type PaymentOutcome =
  | { status: 'paid' }
  | { status: 'pending'; paymentRequestId: string }
  | { status: 'failed'; reason: 'canceled' | 'expired' | 'missing' };

/**
 * Active payment mode for staged rollout. Defaults to on-session.
 * Set VITE_MEWS_PAYMENT_MODE=authorize to keep Option B card form.
 */
export function getMewsPaymentMode(): MewsPaymentMode {
  const env = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
  const mode = env?.VITE_MEWS_PAYMENT_MODE;
  if (mode === 'authorize') return 'authorize';
  return 'on-session';
}

export function isOnSessionPaymentMode(): boolean {
  return getMewsPaymentMode() === 'on-session';
}

/**
 * Base64-encode an absolute return URL for Mews navigator redirects (payment-requests, card-authorization).
 */
export function encodeMewsReturnUrl(absoluteUrl: string): string {
  return btoa(unescape(encodeURIComponent(absoluteUrl)));
}

/**
 * Build Mews payment-requests redirect URL for on-session checkout.
 * @see on-session-payments.md
 */
export function buildPaymentRequestRedirectUrl(
  paymentRequestId: string,
  returnUrl: string,
): string {
  const baseUrl = getMewsApplicationsBaseUrl().replace(/\/$/, '');
  const encodedReturnUrl = encodeMewsReturnUrl(returnUrl);
  return `${baseUrl}/navigator/payment-requests/detail/${paymentRequestId}?returnUrl=${encodeURIComponent(encodedReturnUrl)}`;
}

/**
 * Sum AmountToChargeOnConfirmation.GrossValue across price rows.
 * Returns null when no positive charge amount is present.
 */
export function getAmountToChargeOnConfirmation(
  finalPriceData: GetReservationPriceResponse | null | undefined,
): AmountToCharge | null {
  if (!finalPriceData?.length) return null;

  let grossValue = 0;
  let currency: string | undefined;

  for (const row of finalPriceData) {
    const amount = row.AmountToChargeOnConfirmation;
    const rowGross = amount?.GrossValue;
    if (rowGross == null || rowGross <= 0) continue;
    grossValue += rowGross;
    const rowCurrency = amount?.Currency?.trim();
    if (rowCurrency) {
      currency = rowCurrency;
    }
  }

  if (grossValue <= 0 || !currency) return null;
  return { grossValue, currency };
}

/**
 * True when on-session mode is active and price API indicates a charge at confirmation.
 */
export function canUseOnSessionPayment(
  finalPriceData: GetReservationPriceResponse | null | undefined,
): boolean {
  if (!isOnSessionPaymentMode()) return false;
  return getAmountToChargeOnConfirmation(finalPriceData) != null;
}

/**
 * Classify payment outcome from get reservation group response (requires PaymentRequests extent).
 */
export function resolvePaymentOutcome(
  group: GetReservationGroupResponse,
): PaymentOutcome {
  const paymentRequestId =
    group.PaymentRequestId ?? group.PaymentRequests?.[0]?.Id;

  if (!paymentRequestId) {
    return { status: 'failed', reason: 'missing' };
  }

  const paymentRequest =
    group.PaymentRequests?.find((pr) => pr.Id === paymentRequestId) ??
    group.PaymentRequests?.[0];

  if (!paymentRequest) {
    const hasChargedPayment = group.Payments?.some((p) => p.State === 'Charged');
    if (hasChargedPayment) {
      return { status: 'paid' };
    }
    return { status: 'pending', paymentRequestId };
  }

  if (paymentRequest.State === 'Completed') {
    if (group.Payments?.length) {
      const hasCharged = group.Payments.some((p) => p.State === 'Charged');
      if (!hasCharged) {
        const hasTerminalFailure = group.Payments.some(
          (p) => p.State === 'Failed' || p.State === 'Canceled',
        );
        if (hasTerminalFailure) {
          return { status: 'failed', reason: 'canceled' };
        }
      }
    }
    return { status: 'paid' };
  }

  if (paymentRequest.State === 'Canceled') {
    return { status: 'failed', reason: 'canceled' };
  }

  if (paymentRequest.State === 'Expired') {
    return { status: 'failed', reason: 'expired' };
  }

  return { status: 'pending', paymentRequestId };
}

/**
 * Charged amount from Payments extent, when available.
 */
export function getChargedAmountFromGroup(
  group: GetReservationGroupResponse,
): AmountToCharge | null {
  const charged = group.Payments?.find((p) => p.State === 'Charged');
  const amount = charged?.ChargeAmount ?? charged?.Amount;
  const grossValue = amount?.GrossValue;
  const currency = amount?.Currency?.trim();
  if (grossValue == null || grossValue <= 0 || !currency) return null;
  return { grossValue, currency };
}

/**
 * Build absolute return URL for payment-request redirect (confirmation step).
 */
export function buildPaymentConfirmationReturnUrl(reservationGroupId: string): string {
  const origin =
    typeof window !== 'undefined' ? window.location.origin : 'https://example.com';
  return (
    origin +
    '/book?step=confirmation&reservationGroupId=' +
    encodeURIComponent(reservationGroupId)
  );
}

/** sessionStorage key for an on-session reservation awaiting payment (survives reload / browser Back). */
export const MEWS_PENDING_RESERVATION_GROUP_STORAGE_KEY = 'mewsPendingReservationGroupId';

export function setPendingOnSessionReservationGroupId(reservationGroupId: string): void {
  try {
    sessionStorage.setItem(MEWS_PENDING_RESERVATION_GROUP_STORAGE_KEY, reservationGroupId);
  } catch {
    // Private browsing or storage disabled — URL update still helps browser Back.
  }
}

export function getPendingOnSessionReservationGroupId(): string | null {
  try {
    const id = sessionStorage.getItem(MEWS_PENDING_RESERVATION_GROUP_STORAGE_KEY)?.trim();
    return id || null;
  } catch {
    return null;
  }
}

export function clearPendingOnSessionReservationGroupId(): void {
  try {
    sessionStorage.removeItem(MEWS_PENDING_RESERVATION_GROUP_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Before redirecting to Mews checkout: persist group id and replace history URL so
 * browser Back from Mews lands on confirmation (pending payment / retry), not a fresh booking.
 */
export function markOnSessionPaymentRedirect(reservationGroupId: string): void {
  setPendingOnSessionReservationGroupId(reservationGroupId);
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.set('step', 'confirmation');
  url.searchParams.set('reservationGroupId', reservationGroupId);
  window.history.replaceState(null, '', `${url.pathname}?${url.searchParams.toString()}`);
}
